import { supabase } from '../config/supabase';
import { formatCurrency } from '../utils/currency';

export interface Account {
  id: string;
  business_id: string;
  account_id: string; // Business-specific identifier like A-1234567890-1
  name: string;
  type: 'cash' | 'bank' | 'other';
  balance: number;
  is_default: boolean;
  is_editable: boolean;
  bank_name?: string;
  account_number?: string;
  account_holder_name?: string;
  created_at: string;
  updated_at: string;
}

export interface AccountFormData {
  account_id: string; // Now mandatory
  name: string;
  type: 'cash' | 'bank' | 'other';
  bank_name?: string;
  account_number?: string;
  account_holder_name?: string;
}

export interface AccountTransaction {
  id: string;
  account_id: string;
  amount: number;
  payment_method?: string;
  reference_number?: string;
  description?: string;
  created_at: string;
}

export const accountsService = {
  // Generate a unique account ID for the business
  async generateAccountId(businessId?: string): Promise<string> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required to generate account ID');
      }

      // Get the count of existing accounts for this business
      const { count, error: countError } = await supabase
        .from('accounts')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', businessId);

      if (countError) {
        console.error('Error counting accounts:', countError);
        // Don't throw here, just use 0 as fallback
      }

      // Generate account ID: A-{timestamp}-{count+1}
      const timestamp = Date.now();
      const sequence = (count || 0) + 1;
      const accountId = `A-${timestamp}-${sequence}`;

      console.log('Generated account ID:', accountId);
      return accountId;

    } catch (error) {
      console.error('Error generating account ID:', error);
      // Return a fallback ID instead of throwing
      const fallbackId = `A-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      console.log('Using fallback account ID:', fallbackId);
      return fallbackId;
    }
  },

  // Get all accounts for the business
  async getAccounts(businessId?: string): Promise<{ accounts: Account[]; error: Error | null }> {
    try {
      let query = supabase
        .from('accounts')
        .select('*')
        .order('name', { ascending: true });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { accounts: data || [], error: null };
    } catch (error) {
      return { accounts: [], error: error as Error };
    }
  },

  // Get account by ID
  async getAccountById(id: string, businessId?: string): Promise<{ account: Account | null; error: Error | null }> {
    try {
      let query = supabase
        .from('accounts')
        .select('*')
        .eq('id', id);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query.single();

      if (error) throw error;
      return { account: data, error: null };
    } catch (error) {
      return { account: null, error: error as Error };
    }
  },

  // Get accounts by type
  async getAccountsByType(type: string, businessId?: string): Promise<{ accounts: Account[]; error: Error | null }> {
    try {
      let query = supabase
        .from('accounts')
        .select('*')
        .eq('type', type)
        .order('name', { ascending: true });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { accounts: data || [], error: null };
    } catch (error) {
      return { accounts: [], error: error as Error };
    }
  },

  // Create a new account
  async createAccount(accountData: AccountFormData, businessId?: string): Promise<{ account: Account | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      const { data, error } = await supabase
        .from('accounts')
        .insert([
          {
            business_id: businessId,
            account_id: accountData.account_id,
            name: accountData.name,
            type: accountData.type,
            balance: 0.00,
            is_default: false,
            is_editable: true,
            bank_name: accountData.bank_name,
            account_number: accountData.account_number,
            account_holder_name: accountData.account_holder_name,
          },
        ])
        .single();

      if (error) throw error;
      return { account: data, error: null };
    } catch (error) {
      return { account: null, error: error as Error };
    }
  },

  // Update an existing account
  async updateAccount(id: string, accountData: AccountFormData): Promise<{ account: Account | null; error: Error | null }> {
    try {
      // Check if account is editable
      const { account: currentAccount, error: fetchError } = await this.getAccountById(id);

      if (fetchError) throw fetchError;
      if (!currentAccount) throw new Error('Account not found');

      if (!currentAccount.is_editable) {
        throw new Error('Cannot edit default accounts');
      }

      const { data, error } = await supabase
        .from('accounts')
        .update({
          account_id: accountData.account_id,
          name: accountData.name,
          type: accountData.type,
          bank_name: accountData.bank_name,
          account_number: accountData.account_number,
          account_holder_name: accountData.account_holder_name,
        })
        .eq('id', id)
        .single();

      if (error) throw error;
      return { account: data, error: null };
    } catch (error) {
      return { account: null, error: error as Error };
    }
  },

  // Delete an account
  async deleteAccount(id: string): Promise<{ error: Error | null }> {
    try {
      // Check if account is editable and not default
      const { account: currentAccount, error: fetchError } = await this.getAccountById(id);

      if (fetchError) throw fetchError;
      if (!currentAccount) throw new Error('Account not found');

      if (!currentAccount.is_editable) {
        throw new Error('Cannot delete default accounts');
      }

      if (currentAccount.is_default) {
        throw new Error('Cannot delete default accounts');
      }

      // Check if account has any transactions
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('id')
        .eq('account_id', id)
        .limit(1);

      if (paymentsError) throw paymentsError;

      if (payments && payments.length > 0) {
        throw new Error('Cannot delete account with existing transactions');
      }

      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get account transactions (payments)
  async getAccountTransactions(accountId: string, businessId?: string): Promise<{ transactions: AccountTransaction[]; error: Error | null }> {
    try {
      let query = supabase
        .from('payments')
        .select('*')
        .eq('account_id', accountId)
        .order('created_at', { ascending: false });

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { transactions: data || [], error: null };
    } catch (error) {
      return { transactions: [], error: error as Error };
    }
  },

  // Get account formatted balance
  getFormattedBalance(balance: number): string {
    return formatCurrency(balance);
  },

  // Get account type label
  getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      cash: 'Cash',
      bank: 'Bank',
      other: 'Other',
    };
    return labels[type] || type;
  },

  // Update account balance
  async updateAccountBalance(accountId: string, newBalance: number): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('accounts')
        .update({ balance: newBalance })
        .eq('id', accountId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get account type badge class
  getTypeBadgeClass(type: string): string {
    const classes: Record<string, string> = {
      cash: 'badge-success',
      bank: 'badge-info',
      other: 'badge-secondary',
    };
    return classes[type] || 'badge';
  },
};